<template>
  <v-card outlined class="subscription-card">
    <v-card-text v-if="subscription">
      <v-row>
        <!-- LEFT COLUMN -->
        <v-col cols="12" md="6">
          <!-- 🟢 STATUS -->
          <v-chip
            :color="statusColor"
            dark
            class="text-capitalize font-weight-bold mb-3 status-chip"
          >
            <v-icon left small>mdi-lightning-bolt</v-icon>
            {{ subscription.status }}
          </v-chip>

          <!-- PLAN INFO -->
          <div class="mb-2">
            <v-icon small class="mr-1">mdi-tag</v-icon>
            <strong>Plan:</strong>
            {{ subscription.plan?.nickname || "Multiple Plans" }}
          </div>

          <div class="mb-2">
            <v-icon small class="mr-1">mdi-currency-usd</v-icon>
            <strong>Current Charge:</strong>
            <span class="font-weight-medium">
              {{ displayAmount | currency }}
            </span>
          </div>

          <div class="mb-2 text-capitalize">
            <v-icon small class="mr-1">mdi-calendar-clock</v-icon>
            <strong>Interval:</strong>
            {{ subscription.plan?.interval || "N/A" }}
          </div>
        </v-col>

        <!-- RIGHT COLUMN -->
        <v-col cols="12" md="6">
          <v-sheet color="grey lighten-5" rounded class="pa-3">
            <div
              class="mb-1 font-weight-medium text-uppercase grey--text text--darken-1"
            >
              Billing Period
            </div>
            <div class="font-weight-bold mb-3">
              {{ $moment.unix(subscription.current_period_start).format("LL") }}
              →
              {{ $moment.unix(subscription.current_period_end).format("LL") }}
            </div>

            <!-- Next Invoice (from Stripe) -->
            <div v-if="upcomingInvoice">
              <v-icon small class="mr-1">mdi-receipt</v-icon>
              <strong>Next Invoice:</strong>
              {{
                $moment.unix(upcomingInvoice.next_payment_attempt).format("LL")
              }}
              —
              <strong>{{
                (upcomingInvoice.amount_due / 100) | currency
              }}</strong>
            </div>

            <!-- Fallback if Stripe data unavailable -->
            <div v-else class="grey--text text--darken-1">
              <v-icon small class="mr-1">mdi-receipt</v-icon>
              <strong>Next Invoice:</strong>
              Pending...
            </div>
          </v-sheet>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Skeleton Loader -->
    <v-skeleton-loader v-else type="card" />

    <!-- ACTIONS -->
    <v-card-actions v-if="!hideManagementActions">
      <v-layout row justify-end>
        <v-spacer></v-spacer>
        <v-flex shrink>
          <update-plan :location="location" @updated="loadSubscription" />
        </v-flex>
        <v-flex shrink>
          <update-payment-button
        :locationId="location.id"
        :defaultCardId="subscription?.default_source"
        @update="loadSubscription"
      />
        </v-flex>
      </v-layout>
      
      
    </v-card-actions>
  </v-card>
</template>

<script>
import UpdatePlan from "./UpdatePlan.vue";
import UpdatePaymentButton from "./UpdatePayment.vue";
import locationService from "@/modules/Location/service.js";

export default {
  name: "Subscription",
  components: { UpdatePlan, UpdatePaymentButton },
  props: {
    location: { type: Object, required: true },
    hideManagementActions: { type: Boolean, default: false },
  },
  data() {
    return {
      subscription: null,
      upcomingInvoice: null,
      isLoading: false,
    };
  },
  created() {
    this.loadSubscription();
  },
  computed: {
    displayAmount() {
      // Prefer upcoming invoice’s true amount
      if (this.upcomingInvoice?.amount_due)
        return this.upcomingInvoice.amount_due / 100;

      // fallback to base plan amount
      return this.subscription?.plan?.amount
        ? this.subscription.plan.amount / 100
        : 0;
    },
    statusColor() {
      const s = this.subscription?.status;
      return s === "active"
        ? "green"
        : s === "trialing"
        ? "blue"
        : s === "past_due"
        ? "orange"
        : "red";
    },
  },
  methods: {
    async loadSubscription() {
      this.isLoading = true;
      try {
        const response = await locationService.Billing.getSubscription(
          this.location.id
        );
        this.subscription = response.data;
        await this.loadUpcomingInvoice();
      } catch (error) {
        console.error("Error loading subscription:", error);
      } finally {
        this.isLoading = false;
      }
    },
    async loadUpcomingInvoice() {
      try {
        const res = await locationService.Billing.getUpcomingInvoice(
          this.location.id
        );
        this.upcomingInvoice = res.data;
      } catch (e) {
        console.warn("No upcoming invoice found:", e.message);
        this.upcomingInvoice = null;
      }
    },
  },
};
</script>

<style scoped>
.subscription-card {
  background: linear-gradient(180deg, #fafafa, #fff);
  border: 1px solid #e0e0e0;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.04);
}

.status-chip {
  animation: glow 2.5s infinite ease-in-out;
}
@keyframes glow {
  0%,
  100% {
    box-shadow: 0 0 6px rgba(76, 175, 80, 0.6);
  }
  50% {
    box-shadow: 0 0 12px rgba(76, 175, 80, 0.9);
  }
}

.v-sheet {
  border: 1px dashed #ddd;
}

.v-card-actions {
  background-color: #fafafa;
  border-top: 1px solid #eee;
}

.v-btn {
  text-transform: none;
}
</style>
